import {
  Button,
  EButtonColorVariant,
  EHeadingStyleVariant,
  ETextStyleVariant,
  Heading,
  Text,
  TextInput,
} from '@outdoorsyco/bonfire';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import DateInput from '@/components/common/DateInput/DateInput';
import { trackEvent } from '@/services/track-event';

type ClaimStatusLookupProps = {
  onSubmit?: (claimNumber: string, zipCode: string) => void;
  isLoading?: boolean;
  error?: string;
};

export default function ClaimStatusLookup({
  onSubmit,
  isLoading = false,
  error,
}: ClaimStatusLookupProps) {
  const intl = useIntl();
  const [claimNumber, setClaimNumber] = useState('');
  const [dateOfLoss, setDateOfLoss] = useState('');

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onSubmit?.(claimNumber, dateOfLoss);
  };

  useEffect(() => {
    trackEvent({
      event: 'Claim : Status Lookup Page View',
      pageType: 'Claim',
    });
  }, []);

  return (
    <div className="flex flex-col items-center px-4 pt-16 pb-8 min-h-[80vh] bg-canvas-100 sm:pt-24">
      <Heading variant={EHeadingStyleVariant.H5} className="max-w-md mb-3 text-center">
        <FormattedMessage defaultMessage="Check the status of your Outdoorsy claim." id="8d6hiS" />
      </Heading>

      <Text
        variant={ETextStyleVariant.MediumRegular}
        className="max-w-md mb-4 text-center text-gray-700 md:w-4/12">
        <FormattedMessage
          defaultMessage="Enter your claim number below — see exactly where things stand, no hold music, no waiting."
          id="JQHBxC"
        />
      </Text>

      <form
        onSubmit={handleSubmit}
        className="max-w-sm p-6 bg-white md:w-4/12 rounded-2xl shadow-sm sm:max-w-md sm:p-8">
        <TextInput
          controlled
          className="mb-5"
          name="claim-number"
          label={intl.formatMessage({
            defaultMessage: 'Claim Number',
            id: 'Sc6/nr',
          })}
          inputValue={claimNumber}
          onChange={setClaimNumber}
          markAsRequired
        />

        <DateInput
          controlled
          className="mb-6"
          name="date-of-loss"
          label={intl.formatMessage({
            defaultMessage: 'Date of Loss',
            id: 'Rm8QNO',
          })}
          inputValue={dateOfLoss}
          onChange={setDateOfLoss}
          helpText={intl.formatMessage({
            defaultMessage: '±14 days of actual loss date accepted',
            id: 'l3Dy8Z',
          })}
          markAsRequired
        />

        <Button
          type="submit"
          variant={EButtonColorVariant.Primary}
          fullWidth
          loading={isLoading}
          disabled={!claimNumber || !dateOfLoss}
          label={intl.formatMessage({
            defaultMessage: 'Check your claim status',
            id: '/69mxS',
          })}
        />

        {error && (
          <Text
            variant={ETextStyleVariant.SmallRegular}
            className="pt-4 text-center text-error-500">
            {error}
          </Text>
        )}
      </form>
    </div>
  );
}
